import { StatusMultiSelect } from '@/components/shared';
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { VoucherRedemptionModeFilter, VoucherStatus } from '../types';

const STATUS_OPTIONS: Array<{ value: VoucherStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const REDEMPTION_MODE_OPTIONS: Array<{ value: VoucherRedemptionModeFilter; label: string }> = [
  { value: 'all', label: 'Single and multi-use' },
  { value: 'single_use', label: 'Single use' },
  { value: 'multi_use', label: 'Multi-use' },
];

export interface VouchersFiltersProps {
  selectedStatuses: VoucherStatus[];
  redemptionMode: VoucherRedemptionModeFilter;
  createdByEmail: string;
  creatorOptions: string[];
  onStatusesChange: (value: VoucherStatus[]) => void;
  onRedemptionModeChange: (value: VoucherRedemptionModeFilter) => void;
  onCreatedByEmailChange: (value: string) => void;
}

function formatRedemptionModeSummary(redemptionMode: VoucherRedemptionModeFilter): string {
  return (
    REDEMPTION_MODE_OPTIONS.find((option) => option.value === redemptionMode)?.label ??
    'Single and multi-use'
  );
}

export function VouchersFilters({
  selectedStatuses,
  redemptionMode,
  createdByEmail,
  creatorOptions,
  onStatusesChange,
  onRedemptionModeChange,
  onCreatedByEmailChange,
}: VouchersFiltersProps) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [redemptionModeOpen, setRedemptionModeOpen] = useState(false);
  const redemptionModeMenuRef = useRef<HTMLDivElement>(null);
  const redemptionModeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!redemptionModeOpen) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        redemptionModeMenuRef.current?.contains(target) ||
        redemptionModeButtonRef.current?.contains(target)
      ) {
        return;
      }
      if (redemptionModeOpen) setRedemptionModeOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (redemptionModeOpen) {
          setRedemptionModeOpen(false);
          redemptionModeButtonRef.current?.focus();
        }
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [redemptionModeOpen]);

  return (
    <div className="relative z-10 flex flex-wrap items-end gap-3 border-b border-neutral-200 bg-white px-5 py-4">
      <StatusMultiSelect
        label="Status"
        options={STATUS_OPTIONS}
        selectedStatuses={selectedStatuses}
        onStatusesChange={onStatusesChange}
        open={statusOpen}
        onOpenChange={(open) => {
          if (open) setRedemptionModeOpen(false);
          setStatusOpen(open);
        }}
      />

      <div className="relative flex min-w-[180px] flex-col gap-1 text-small font-medium text-neutral-700">
        <span>Use type</span>
        <button
          ref={redemptionModeButtonRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={redemptionModeOpen}
          aria-label={formatRedemptionModeSummary(redemptionMode)}
          className="focus-ring flex h-10 items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 text-left text-body font-normal text-neutral-900"
          onClick={() => {
            setStatusOpen(false);
            setRedemptionModeOpen((open) => !open);
          }}
        >
          <span>{formatRedemptionModeSummary(redemptionMode)}</span>
          <ChevronDown className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        </button>
        {redemptionModeOpen ? (
          <div
            ref={redemptionModeMenuRef}
            role="menu"
            className="absolute left-0 top-full z-20 mt-1 w-56 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
          >
            {REDEMPTION_MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={redemptionMode === option.value}
                className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-body font-normal text-neutral-800 hover:bg-backoffice-50"
                onClick={() => {
                  onRedemptionModeChange(option.value);
                  setRedemptionModeOpen(false);
                  redemptionModeButtonRef.current?.focus();
                }}
              >
                <span>{option.label}</span>
                {redemptionMode === option.value ? (
                  <Check className="h-4 w-4 text-backoffice-700" aria-hidden="true" />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <label className="flex min-w-[220px] flex-col gap-1 text-small font-medium text-neutral-700">
        Created by
        <select
          value={createdByEmail}
          onChange={(event) => onCreatedByEmailChange(event.target.value)}
          className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body font-normal text-neutral-900"
        >
          <option value="">All creators</option>
          {creatorOptions.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
